import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, getToken } from "@/api/client";
import { connectSendbird, disconnectSendbird, fetchSessionToken } from "@/lib/sendbird";

/**
 * SendbirdSessionProvider + useSendbird
 * -------------------------------------
 * Single source of truth for the current user's Sendbird session. Mounted
 * once high in the tree (inside AuthProvider) so every consumer — the shared
 * <SendbirdRoot> UIKit provider, embedded channels, admin panels, and the
 * notification bell — observes the exact same {ready, session, unreadCount}
 * state. Previously each caller ran its own effect, which caused a race where
 * <GroupChannel> could render before the UIKit <SendbirdProvider> mounted.
 */
const SendbirdContext = createContext({
    session: null,
    ready: false,
    error: null,
    unreadCount: 0,
    refreshUnread: async () => {},
});

export function SendbirdSessionProvider({ children }) {
    const { user } = useAuth();
    const [session, setSession] = useState(null);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);

    const refreshUnread = useCallback(async () => {
        try {
            const { data } = await api.get("/sendbird/notifications/unread-count");
            setUnreadCount(Number(data?.unread_count || 0));
        } catch (_e) { /* silent — bell just doesn't update */ }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const hasToken = !!getToken();
            if (!user || !hasToken) {
                await disconnectSendbird();
                setSession(null);
                setReady(false);
                setUnreadCount(0);
                return;
            }
            try {
                const s = await fetchSessionToken();
                if (cancelled) return;
                setSession(s);
                await connectSendbird({ userId: s.user_id, sessionToken: s.session_token });
                if (cancelled) return;
                setReady(true);
                setError(null);
                await refreshUnread();
            } catch (err) {
                if (cancelled) return;
                console.warn("[sendbird] init failed:", err);
                setError(err);
                setReady(false);
            }
        })();
        return () => { cancelled = true; };
    }, [user, refreshUnread]);

    // Poll unread count every 45s while connected (Sendbird SDK also pushes via websocket,
    // but a lightweight poll gives us a reliable fallback if the WS drops).
    useEffect(() => {
        if (!ready) return undefined;
        const id = setInterval(refreshUnread, 45_000);
        return () => clearInterval(id);
    }, [ready, refreshUnread]);

    const value = { session, ready, error, unreadCount, refreshUnread };
    return <SendbirdContext.Provider value={value}>{children}</SendbirdContext.Provider>;
}

export function useSendbird() {
    return useContext(SendbirdContext);
}
